import { useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import LanguageSwitcher from '../../../components/ui/LanguageSwitcher';
import { ArrowUpRight } from '../../../components/ui/icons/ArrowUpRight';
import blackLogo from '../../../app/assets/img/logoSORBlack.svg?url';

const Bar = styled.header`
  position: fixed;
  z-index: 100;
  top: 20px;
  left: var(--gutter);
  right: var(--gutter);
  max-width: 1320px;
  margin: 0 auto;
  padding: 10px 14px 10px 22px;
  min-height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  background: rgba(255, 255, 255, 0.93);
  backdrop-filter: blur(18px);
  border: 1px solid var(--line);
  border-radius: 6px;
  box-shadow: 0 4px 20px #20212205;
  @media (max-width: 760px) {
    top: 14px;
    left: 16px;
    right: 16px;
    padding: 8px 10px 8px 18px;
    gap: 14px;
    min-height: 60px;
  }
`;
const Brand = styled(Link)`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  img {
    display: block;
    width: 100px;
    height: 32px;
    object-fit: contain;
  }
`;
const Navigation = styled.nav`
  display: flex;
  align-items: center;
  gap: 32px;
  margin-left: auto;
  a {
    font-size: 1.3rem;
    color: var(--muted);
    text-decoration: none;
    padding: 10px 0;
  }
  a.active,
  a:hover {
    color: var(--ink);
  }
  a.active {
    text-decoration: underline;
    text-underline-offset: 6px;
  }
  @media (max-width: 900px) {
    display: none;
  }
`;
const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;
const ContactLink = styled(Link)`
  font-size: 1.3rem;
  padding: 11px 18px;
  border: 1px solid var(--line);
  border-radius: 999px;
  text-decoration: none;
  background: var(--surface);
  &:hover {
    background: var(--surface-strong);
  }
  @media (max-width: 760px) {
    display: none;
  }
`;
const MenuButton = styled.button`
  background: none;
  border: 0;
  width: 44px;
  height: 44px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 6px;
  span {
    height: 1px;
    width: 22px;
    background: var(--ink);
  }
`;
const Menu = styled.dialog`
  margin: 0 0 0 auto;
  max-height: 100dvh;
  height: 100dvh;
  max-width: 100vw;
  width: min(560px, 100vw);
  border: 0;
  border-left: 1px solid var(--line);
  padding: 36px clamp(24px, 5vw, 64px);
  background: var(--paper);
  color: var(--ink);
  &::backdrop {
    background: #20212230;
    backdrop-filter: blur(4px);
  }
  .menu-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 1.2rem;
    letter-spacing: 0.12em;
    color: var(--muted);
  }
  .close {
    width: 44px;
    height: 44px;
    background: var(--surface);
    border: 1px solid var(--line);
    border-radius: 50%;
    font-size: 2.5rem;
  }
  nav {
    display: flex;
    flex-direction: column;
    margin: 64px 0;
  }
  nav a {
    font-size: clamp(2.8rem, 4vw, 4.8rem);
    letter-spacing: -0.05em;
    padding: 12px 0;
    text-decoration: none;
    border-bottom: 1px solid var(--line);
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  nav small {
    font-size: 1.2rem;
    letter-spacing: 0;
    color: var(--muted);
  }
  nav a:hover,
  nav a.active {
    color: #777;
  }
  .menu-foot {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 1.4rem;
  }
`;
export default function Header() {
  const { t, i18n } = useTranslation('common');
  const lang = i18n.language.startsWith('fr') ? 'fr' : 'en';
  const fr = lang === 'fr';
  const location = useLocation();
  const dialog = useRef<HTMLDialogElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const home = fr ? '/fr' : '/';
  const links = [
    { path: home, label: t('nav.home') },
    { path: `/${lang}/services`, label: t('nav.services') },
    {
      path: `/${lang}/${fr ? 'nos-realisations' : 'our-work'}`,
      label: t('nav.showcase'),
    },
    { path: `/${lang}/${fr ? 'a-propos' : 'about'}`, label: t('nav.about') },
    { path: `/${lang}/contact`, label: t('nav.contact') },
    { path: `/${lang}/app/login`, label: t('nav.login') },
  ];
  const close = () => dialog.current?.close();
  useEffect(() => {
    close();
  }, [location.pathname]);
  return (
    <>
      <Bar>
        <Brand to={home} aria-label="SOR SEO home">
          <img src={blackLogo} alt="SOR SEO" />
        </Brand>
        <Navigation
          aria-label={fr ? 'Navigation principale' : 'Main navigation'}
        >
          {links.slice(1, 4).map((link) => (
            <NavLink key={link.path} to={link.path}>
              {link.label}
            </NavLink>
          ))}
        </Navigation>
        <Actions>
          <LanguageSwitcher />
          <ContactLink to={`/${lang}/contact`}>
            {fr ? 'Parlons-en' : 'Let’s talk'} <ArrowUpRight />
          </ContactLink>
          <MenuButton
            ref={trigger}
            onClick={() => dialog.current?.showModal()}
            aria-label={fr ? 'Ouvrir le menu' : 'Open menu'}
            aria-haspopup="dialog"
          >
            <span />
            <span />
          </MenuButton>
        </Actions>
      </Bar>
      <Menu
        ref={dialog}
        aria-label={fr ? 'Navigation' : 'Navigation menu'}
        onClick={(event) => {
          if (event.target === event.currentTarget) {
            const bounds = event.currentTarget.getBoundingClientRect();
            if (event.clientX < bounds.left) close();
          }
        }}
        onClose={() => trigger.current?.focus()}
      >
        <div className="menu-top">
          <span>SOR SEO</span>
          <button
            className="close"
            onClick={close}
            aria-label={fr ? 'Fermer le menu' : 'Close menu'}
          >
            ×
          </button>
        </div>
        <nav>
          {links.map((link, index) => (
            <NavLink end key={link.path} to={link.path} onClick={close}>
              {link.label}
              <small>0{index + 1}</small>
            </NavLink>
          ))}
        </nav>
        <div className="menu-foot">
          <a href="mailto:contact@sor-seo.com">
            contact@sor-seo.com <ArrowUpRight />
          </a>
          <LanguageSwitcher />
        </div>
      </Menu>
    </>
  );
}
